import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAssetPath } from '../utils/assetHelper';
import './SettingsNotificationsPage.scss';

interface NotificationPreference {
  id: string;
  label: string;
  enabled: boolean;
}

interface NotificationChannel {
  id: string;
  label: string;
  enabled: boolean;
  description?: string;
}

const SettingsNotificationsPage: React.FC = () => {
  // State for notification preferences
  const [preferences, setPreferences] = useState<NotificationPreference[]>([
    { id: 'gameUpdates', label: 'Game Updates', enabled: true },
    { id: 'transactionAlerts', label: 'Transaction Alerts', enabled: false },
    { id: 'communityEvents', label: 'Community Events', enabled: true },
    { id: 'specialOffers', label: 'Special Offers', enabled: false }
  ]);

  // State for notification channels
  const [channels, setChannels] = useState<NotificationChannel[]>([
    { id: 'inApp', label: 'In-app', enabled: false },
    { id: 'email', label: 'Email', enabled: true },
    { id: 'pushNotifications', label: 'Push Notifications', enabled: true, description: '(for mobile users)' }
  ]);

  // State for do not disturb mode
  const [doNotDisturb, setDoNotDisturb] = useState(false);

  // Handle preference toggle
  const handlePreferenceToggle = (id: string) => {
    setPreferences(prev => 
      prev.map(pref => 
        pref.id === id ? { ...pref, enabled: !pref.enabled } : pref
      )
    );
    
    // Show success notification (in a real app, this would save to backend)
    showNotificationSuccess();
  };

  // Handle channel toggle
  const handleChannelToggle = (id: string) => {
    setChannels(prev => 
      prev.map(channel => 
        channel.id === id ? { ...channel, enabled: !channel.enabled } : channel
      )
    );
    
    // Show success notification (in a real app, this would save to backend)
    showNotificationSuccess();
  };

  // Handle do not disturb toggle
  const handleDoNotDisturbToggle = () => {
    setDoNotDisturb(prev => !prev);
    
    // Show success notification (in a real app, this would save to backend)
    showNotificationSuccess();
  };

  // Show success notification (placeholder for actual notification system)
  const showNotificationSuccess = () => {
    console.log('Notification preferences saved successfully');
    // In a real app, this would trigger a toast notification
  };

  // Set document title on component mount
  useEffect(() => {
    document.title = 'Settings - Notifications | Phantom Clash';
    
    // Initialize tooltips if Bootstrap is available
    if ((window as any).bootstrap) {
      const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
      tooltipTriggerList.map(function(tooltipTriggerEl) {
        return new (window as any).bootstrap.Tooltip(tooltipTriggerEl);
      });
    }
  }, []);

  return (
    <>
      {/* Page Menu */}
      <div className="c-page-menu">
        <ul>
          <li><Link to="/settings-profile">Profile</Link></li>
          <li className="active"><Link to="/settings-notifications">Notifications</Link></li>
          <li><Link to="/settings-wallet">Wallet</Link></li>
          <li><Link to="/settings-security">Security</Link></li>
          <li><Link to="/settings-privacy">Privacy</Link></li>
          <li><Link to="/settings-display">Theme & Display</Link></li>
          <li><Link to="/settings-advanced">Advanced Settings</Link></li>
        </ul>
      </div>

      <div className="d-block w-100">
        <div className="box position-relative z-1 overflow-hidden mt-2">
          <div className="c-content-box c-content-box--xspadding c-content-box--setting-width">
            <div className="c-content-box__title">
              <h1 style={{ backgroundImage: 'none' }}><i className="bi bi-bell-fill"></i> Notification Preferences</h1>
              <div className="c-content-box__title-info"></div>
            </div>
            <div className="c-content-box__content">
              <div className="c-content-box__checkboxlist">
                {/* Notification Preferences */}
                {preferences.map((pref, index) => (
                  <div 
                    key={pref.id} 
                    className={`c-content-box__checkboxlist-item ${index === 0 ? 'pt-0' : 'c-divider'}`}
                  >
                    <span>{pref.label}</span>
                    <label className="c-switch" id={pref.id}>
                      <input 
                        type="checkbox" 
                        className="c-switch-input" 
                        checked={pref.enabled}
                        onChange={() => handlePreferenceToggle(pref.id)}
                      />
                      <span className="c-switch-label" data-on="On" data-off="Off"></span>
                      <span className="c-switch-handle"></span>
                    </label>
                  </div>
                ))}

                {/* Notification Delivery */}
                <div className="c-content-box__checkboxlist-item flex-column align-items-baseline c-divider pb-md-4 pb-3">
                  <h3 className="mb-md-4 mb-3">Notification Delivery</h3>
                  <div className="d-md-flex d-block align-items-center">
                    {channels.map(channel => (
                      <div key={channel.id} className="me-4 mb-md-0 mb-3">
                        <input 
                          className="c-checkbox" 
                          id={channel.id} 
                          type="checkbox" 
                          checked={channel.enabled}
                          onChange={() => handleChannelToggle(channel.id)}
                        />
                        <label className="c-checkbox__label" htmlFor={channel.id}>
                          {channel.label} {channel.description && <small>{channel.description}</small>}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Do Not Disturb */}
                <div className="c-content-box__checkboxlist-item c-divider">
                  <span>
                    <i className="bi bi-moon-fill"></i> Do Not Disturb
                    <small className="ms-2">Silence notifications during specified hours</small>
                  </span>
                  <label className="c-switch" id="doNotDisturb">
                    <input 
                      type="checkbox" 
                      className="c-switch-input" 
                      checked={doNotDisturb}
                      onChange={handleDoNotDisturbToggle}
                    />
                    <span className="c-switch-label" data-on="On" data-off="Off"></span>
                    <span className="c-switch-handle"></span>
                  </label>
                </div>

                {/* Time Range (only visible when Do Not Disturb is enabled) */}
                {doNotDisturb && (
                  <div className="c-content-box__checkboxlist-item flex-column align-items-baseline c-divider pb-md-4 pb-3">
                    <div className="d-md-flex d-block align-items-center w-100 mt-3">
                      <div className="c-input-group me-3 mb-md-0 mb-3">
                        <label htmlFor="startTime" className="c-form-label c-form-label--gray">From</label>
                        <input 
                          type="time" 
                          id="startTime" 
                          className="c-form-input" 
                          defaultValue="22:00" 
                        />
                      </div>
                      <div className="c-input-group">
                        <label htmlFor="endTime" className="c-form-label c-form-label--gray">To</label>
                        <input 
                          type="time" 
                          id="endTime" 
                          className="c-form-input" 
                          defaultValue="07:00" 
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsNotificationsPage; 